import { useState } from 'react';
import { User, Mail, Building2, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AvatarUpload } from '@/components/ui/AvatarUpload';
import { useAuthStore } from '@/stores/auth.store';
import { usersApi } from '@/lib/api';

const ACCOUNTS_URL = 'https://accounts.soliddigital.com';

export function SettingsProfile() {
  const { user, updateAvatarUrl } = useAuthStore();
  const [isRemoving, setIsRemoving] = useState(false);

  // Get user's primary organization
  const primaryOrg = user?.organizations?.find((org: any) => org.type === 'AGENCY')
    || user?.organizations?.[0];

  const handleUpload = async (file: File): Promise<string> => {
    const url = await usersApi.uploadAvatar(file);
    updateAvatarUrl(url);
    return url;
  };

  const handleRemovePhoto = async () => {
    setIsRemoving(true);
    try {
      await usersApi.deleteAvatar();
      updateAvatarUrl(null);
    } catch (error) {
      console.error('Failed to remove avatar:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <Card className="p-6">
        <h2 className="text-heading-3 text-secondary">Your Profile</h2>

        {/* A fixed avatar beside a flexible column: on a 390px screen the pair
            needed 413px. It stacks on a phone, and the info column may shrink
            (mobile audit, 2026-08-28). */}
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:gap-6">
          {/* Avatar */}
          <div className="shrink-0">
            <AvatarUpload
              currentAvatarUrl={user?.avatarUrl}
              name={user?.name || 'User'}
              onUpload={handleUpload}
            />
            {user?.avatarUrl && (
              <button
                onClick={handleRemovePhoto}
                disabled={isRemoving}
                className="mt-2 flex items-center gap-1 text-xs text-grey-500 hover:text-red-500 transition-colors mx-auto disabled:opacity-50"
              >
                {isRemoving ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Trash2 className="w-3 h-3" />
                )}
                Remove photo
              </button>
            )}
          </div>

          {/* Profile Info */}
          <div className="w-full min-w-0 flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-grey-500 mb-1">
                Full Name
              </label>
              <div className="flex items-center gap-2 text-secondary">
                <User className="w-4 h-4 text-grey-400" />
                {user?.name || 'Not set'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-grey-500 mb-1">
                Email Address
              </label>
              <div className="flex items-center gap-2 text-secondary">
                <Mail className="w-4 h-4 text-grey-400" />
                {user?.email}
              </div>
            </div>

            {primaryOrg && (
              <div>
                <label className="block text-sm font-medium text-grey-500 mb-1">
                  Organization
                </label>
                <div className="flex items-center gap-2 text-secondary">
                  <Building2 className="w-4 h-4 text-grey-400" />
                  {primaryOrg.name}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edit Profile Info */}
        <div className="mt-6 pt-6 border-t border-grey-100">
          <p className="text-sm text-grey-500 mb-4">
            Your profile information is managed through the Solid Digital account portal.
          </p>
          <Button
            variant="outline"
            onClick={() => window.open(ACCOUNTS_URL, '_blank')}
          >
            <ExternalLink className="w-4 h-4" />
            Manage Profile
          </Button>
        </div>
      </Card>

      {/* Organizations Card */}
      {user?.organizations && user.organizations.length > 0 && (
        <Card className="p-6">
          <h2 className="text-heading-3 text-secondary mb-4">Your Organizations</h2>
          <p className="text-sm text-grey-500 mb-4">
            Organizations you are a member of
          </p>

          <div className="divide-y divide-grey-100">
            {user.organizations.map((org: any) => (
              <div key={org.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-grey-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-grey-500" />
                  </div>
                  <div>
                    <div className="font-medium text-secondary">{org.name}</div>
                    <div className="text-xs text-grey-500">
                      {org.type === 'CLIENT' ? 'Client' : 'Agency'}
                      {org.isOwner && ' • Owner'}
                    </div>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-grey-100 text-grey-600">
                  {org.role || 'Member'}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
